import { List, OrderedMap, Set, Map } from 'immutable';
import { compose } from 'redux';
import moment from 'moment';
import {
  DocumentFactory,
  DocumentsFactory,
  ViewinfoFactory,
  ValidatedDocumentFactory,
  ApprovalStampFactory,
} from '@/domain/documents/factories';
import { ApprovalsStoreAdapter } from '@/domain/approvals/helpers';
import CONST, { GRID_PER_PAGE_DEFAULT } from '@/domain/documents/constants';
import { storage } from '@/lib/storage';

const TypeTags = {
  _T_FIN: 'financial',
  _T_GEN: 'general',
};

const OptionalTags = {
  _S_PROTECTED: 'protected',
};

// The document's currently-assigned sub-type tag (_S_IS_*), or undefined. At most one per document.
export const getCurrentSubtype = (tags) => (tags ? tags.find((tag) => tag.startsWith('_S_IS_')) : undefined);

const PairsTagsStatuses = [
  {
    status: 'signed',
    tags: Set.of('_S_SIG_DOKKA1_SIGNED', '_S_SIG_DOKKA2_SIGNED'),
  },
  {
    status: 'pending',
    tags: Set.of('_S_SIG_DOKKA1_PENDING', '_S_SIG_DOKKA2_PENDING'),
  },
  {
    status: 'pending',
    tags: Set.of('_S_SIG_DOKKA1_SIGNED', '_S_SIG_DOKKA2_PENDING'),
  },
  {
    status: 'pending',
    tags: Set.of('_S_SIG_DOKKA1_PENDING', '_S_SIG_DOKKA2_SIGNED'),
  },
];

const StatusTags = {
  _S_NEW: 'new',
  _S_READ: 'read',
  _S_ACCEPTED: 'accepted',
  _S_REJECTED: 'rejected',
  _S_SIG_ORIGINAL: 'signed',
  _S_PREVIEW_STATUS: 'isBeingProcessed',
  _S_SCHEDULED_ACCEPTANCE: 'isScheduledAcceptance',
};

const pairsTags = PairsTagsStatuses.map((pair) => pair.tags).reduce((res, tags) => res.concat(tags), new Set());

const StatusTagsSet = Set.fromKeys(StatusTags).concat(pairsTags);

function checkStatus(tags) {
  const statusTags = StatusTagsSet.intersect(tags);
  let statuses = statusTags.map((tag) => StatusTags[tag]);
  PairsTagsStatuses.forEach((pair) => {
    if (pair.tags.intersect(tags).size === pair.tags.size) {
      statuses = statuses.add(pair.status);
    }
  });
  return statuses.toArray();
}

// eslint-disable-next-line max-len
export function parseTags(document) {
  // we know tags is Array, not Set
  let tags = document.tags || [];
  tags = tags.map((item) => item.trim());
  // @to-do quick fallback. return empty values for type and status if no tags present
  const TypeTagsSet = Set.fromKeys(TypeTags);
  const typeKey = TypeTagsSet.intersect(tags).toArray();
  const OptionalTagsSet = Set.fromKeys(OptionalTags);
  const protectedKey = OptionalTagsSet.intersect(tags).toArray();
  return Object.assign(document, {
    doctype: typeKey.length ? TypeTags[typeKey[0]] : undefined,
    status: checkStatus(tags),
    protected: protectedKey.length ? Boolean(OptionalTags[protectedKey[0]]) : false,
  });
}

function castTagsToSet(document) {
  const castedDocument = document;
  if (document && Array.isArray(document.tags)) {
    castedDocument.tags = Set(castedDocument.tags);
  }
  castedDocument.approvals = document.approvals || new Map();
  return castedDocument;
}

function castSnakeCaseProps(document) {
  const { notes_color: notesColor, ...tail } = document;
  return { ...tail, notesColor };
}

export function documentCompositionFunctionsAdapter(d) {
  return compose(DocumentFactory, castTagsToSet, parseTags, castSnakeCaseProps)(d);
}

export function documentsListAdapter(l) {
  // eslint-disable-next-line max-len
  return l.reduce((a, v) => a.set(v.documentID, documentCompositionFunctionsAdapter(v)), new OrderedMap());
}

export function documentsAdapter({
  items,
  pageToken,
  count,
  processing,
  // eslint-disable-next-line camelcase
  company_total,
  // eslint-disable-next-line camelcase
  count_without_link_panels,
}) {
  return DocumentsFactory({
    list: documentsListAdapter(items),
    pageToken,
    count,
    processing,
    company_total,
    count_without_link_panels,
  });
}

export function viewInfoAdapter({ pages, rotations }) {
  const positiveRotations = (rotations || []).map((angle) => (angle < 0 ? angle + 360 : angle));
  return ViewinfoFactory({
    pages,
    rotations: List(positiveRotations),
  });
}

export const ApprovalStampAdapter = (stamp) => ({
  signPosition: ApprovalStampFactory(stamp),
});

export function documentAdapter(document) {
  return {
    document: DocumentFactory({
      ...compose(castTagsToSet, parseTags, castSnakeCaseProps)(document),
      viewinfo: viewInfoAdapter(document.viewinfo),
      sign: ApprovalStampAdapter(document.sign.sign_position),
    }),
  };
}

// eslint-disable-next-line max-len
export function documentAdapterValidated(document) {
  return {
    document: ValidatedDocumentFactory({
      ...compose(castTagsToSet, parseTags, castSnakeCaseProps)(document),
      viewinfo: viewInfoAdapter(document.viewinfo),
      approvals: ApprovalsStoreAdapter(document.approvals),
      // eslint-disable-next-line max-len
      acceptValidationError: document.details && document.details.error ? document.details.error : null,
      sign: ApprovalStampAdapter(document.sign.sign_position),
    }),
  };
}

export function sorterByCreated(a, b) {
  const aCreated = new Date(a.created).getTime();
  const bCreated = new Date(b.created).getTime();

  return bCreated === aCreated ? a.documentID.localeCompare(b.documentID) : bCreated - aCreated;
}

export const sorterByFavoriteOrCreated = (a, b) =>
  // $FlowFixMe
  b.favorite - a.favorite || sorterByCreated(a, b);

export function documentGroup(list) {
  return OrderedMap(list.groupBy((e) => moment(e.created).format('MM-YYYY')));
}

// @to-do use function composition for next 2 functions
export function documentsWithUniqLinkedUngrouped(d) {
  // filter linked document and group by linkID
  const groupedLinkedDocs = d.filter((doc) => doc.linkid).groupBy((doc) => doc.linkid);

  // sort linked documents by favorites and created fields
  const uniqLinkedDocs = groupedLinkedDocs.reduce((a, v, key) => {
    // favorite => fresh doc => old doc - always
    const sortedLinkedList = v.sort(sorterByFavoriteOrCreated);

    return a.set(key, sortedLinkedList);
  }, new OrderedMap());

  // list of linked ids which were set
  let alreadySetCoverForLinkedIDs = new List();

  return d.reduce((list, document) => {
    if (alreadySetCoverForLinkedIDs.includes(document.linkid)) return list;

    if (document.linkid) {
      alreadySetCoverForLinkedIDs = alreadySetCoverForLinkedIDs.push(document.linkid);

      if (!document.favorite) {
        const linkedDocuments = uniqLinkedDocs.get(document.linkid);
        // first document will be always favorite or more fresh by date
        return list.push(linkedDocuments.first());
      }
    }

    return list.push(document);
  }, new List());
}

export const documentsWithUniqLinked = (d) => {
  const list = documentsWithUniqLinkedUngrouped(d);

  return documentGroup(list);
};

export function documentsWithoutLinked(d) {
  return d.filter((doc) => !doc.linkid);
}

export const acceptedSignTypes = Set(['pdf', 'jpg', 'jpeg', 'png', 'docx', 'xlsx', 'xlsm', 'pptx', 'tiff', 'tif']);

export function checkAcceptSignType(id) {
  const extension = id.split('.').pop();
  return acceptedSignTypes.has(extension);
}

export const isDocPaid = (doc) => doc.tags.includes(CONST.tags.paid);
export const isDocPaymentInProgress = (doc) => doc.tags.includes(CONST.tags.paymentInProgress);

export const isDocTrash = (doc) => doc.tags.includes(CONST.tags.trash);

export const allowedExportFormatsForGrid = ['csv', 'xlsx'];

export const getCurrentStoredConfig = () => {
  const record = storage().workspaceAgGridConfig().get();
  return record ? JSON.parse(record) : {};
};

export const updateGridConfigChanges = (params) => {
  const defaultState = {
    gridColumns: null,
    gridSorting: null,
    gridFilters: null,
  };

  // companyId, currentPresetId
  const currentStoredConfig = getCurrentStoredConfig();
  const storeCurrentConfig =
    currentStoredConfig.companyId === params.companyId && currentStoredConfig.currentPresetId === params.currentPresetId
      ? currentStoredConfig
      : {};

  const update = { ...defaultState, ...storeCurrentConfig, ...params };
  storage().workspaceAgGridConfig().set(JSON.stringify(update));
};

export const deleteGridConfigChanges = () => {
  storage().workspaceAgGridConfig().remove();
};

const getCurrentPublishTypeID = (userId) => `publishType-${userId}`;

export const getCurrentPublishType = (userId) => window.localStorage.getItem(getCurrentPublishTypeID(userId));

export const updateCurrentPublishType = (userId, publishType) =>
  window.localStorage.setItem(getCurrentPublishTypeID(userId), publishType);

export const decodeArrayBufferText = (value) => {
  try {
    const dataView = new DataView(value);
    const decoder = new TextDecoder('utf8');
    return JSON.parse(decoder.decode(dataView));
  } catch (e) {
    return e;
  }
};

export const mergeDocumentsMapWithPreviousGridMeta = (originList, documentsWereChangedList) =>
  originList.mergeWith((oldVal, newVal) => newVal.set('gridMeta', oldVal.gridMeta), documentsWereChangedList);

export const getWorkspaceGridPerPage = () => {
  const stored = storage().workspaceAgGridPerPage().get();

  return stored ? JSON.parse(stored) : GRID_PER_PAGE_DEFAULT;
};

export const setWorkspaceGridPerPage = (value) => {
  storage().workspaceAgGridPerPage().set(JSON.stringify(value));
};
